import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { AdminCourseCatalogResponse, AdminCourseSummary, AdminLessonSummary } from '../../core/models/portal.models';

const VIDEO_LANGUAGES: Record<string, string> = {
  de: 'Deutsch',
  en: 'Englisch',
  tr: 'Türkisch',
  ar: 'Arabisch',
  uk: 'Ukrainisch',
  ru: 'Russisch',
  pl: 'Polnisch',
  ps: 'Paschtu'
};

@Component({
  selector: 'app-course-videos-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './course-videos-page.component.html'
})
export class CourseVideosPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);

  protected readonly catalog = signal<AdminCourseCatalogResponse | null>(null);
  protected readonly loading = signal(false);
  protected readonly importing = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly notice = signal<string | null>(null);
  protected readonly selectedCourseId = signal<number | null>(null);
  protected readonly courses = computed(() => this.catalog()?.courses ?? []);
  protected readonly selectedCourse = computed(() =>
    this.courses().find((course) => course.id === this.selectedCourseId()) ?? this.courses()[0] ?? null
  );

  ngOnInit(): void {
    this.loadCatalog();
  }

  protected loadCatalog(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.adminCourseCatalog().subscribe({
      next: (catalog) => {
        this.catalog.set(catalog);
        this.loading.set(false);
        if (!this.selectedCourseId() && catalog.courses.length) {
          this.selectedCourseId.set(catalog.courses[0].id);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Die importierten Kurs- und Videozuordnungen konnten nicht geladen werden.');
      }
    });
  }

  protected importContent(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.error.set('Bitte eine .xlsx-Datei auswählen.');
      return;
    }

    this.importing.set(true);
    this.error.set(null);
    this.notice.set(null);
    this.api.importCourseContent(file).subscribe({
      next: (result) => {
        this.importing.set(false);
        this.notice.set(`Gesamtimport abgeschlossen: ${result.questionsCreated} Fragen angelegt, ${result.questionsUpdated} aktualisiert; ${result.videosWithUrl} Videos mit URL verarbeitet.`);
        this.loadCatalog();
      },
      error: (response) => {
        this.importing.set(false);
        this.error.set(response?.error?.message || 'Der Gesamtimport konnte nicht durchgeführt werden.');
      }
    });
  }

  protected selectCourse(course: AdminCourseSummary): void {
    this.selectedCourseId.set(course.id);
  }

  protected languageLabel(language: string): string {
    return VIDEO_LANGUAGES[language] ?? language;
  }

  protected languageSummary(lesson: AdminLessonSummary): string {
    return lesson.videos
      .filter((video) => video.active)
      .map((video) => this.languageLabel(video.language))
      .join(', ');
  }

  protected librarySummary(lesson: AdminLessonSummary): string {
    return [...new Set(lesson.videos.filter((video) => video.active).map((video) => video.libraryId).filter(Boolean))].join(', ') || '-';
  }

  protected videoCount(course: AdminCourseSummary): number {
    return course.lessons.reduce((total, lesson) => total + lesson.videos.filter((video) => video.active).length, 0);
  }

  protected lessonCountWithVideos(course: AdminCourseSummary): number {
    return course.lessons.filter((lesson) => lesson.videos.some((video) => video.active)).length;
  }

  protected questionCount(course: AdminCourseSummary): number {
    return course.finalQuestions.length + course.lessons.reduce((total, lesson) => total + lesson.questions.length, 0);
  }
}
